/* global globalThis */
/**
 * Chiffrement des activités, version 2 — implémentation de référence
 * WebCrypto (navigateur et Node ≥ 20). La spécification complète et les
 * vecteurs de test partagés avec l'app (Kotlin) sont dans
 * notAlone/docs/architecture/ACTIVITY-CRYPTO-V2.md.
 *
 * Principe : tout le contenu d'une activité est chiffré avec une clé
 * d'activité (32 octets) générée sur le téléphone. Le serveur ne stocke que
 * des enveloppes de cette clé, qu'il ne peut pas ouvrir.
 */

const subtle = () => globalThis.crypto.subtle;
const utf8 = text => new TextEncoder().encode(text);

export const SALT = 'MonAlerte/activity/v2';
export const ENVELOPE_VERSION = 0x02;
export const RECIPIENT = Object.freeze({ DEVICE: 1, LINK: 2, PASSWORD: 3, ESCROW: 4 });
export const PASSWORD_ITERATIONS = 600_000;
const NONCE_BYTES = 12;
const RAW_P256_PUBLIC_BYTES = 65;

// ---------- encodages ----------

export function concatBytes(...parts) {
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    parts.forEach(part => { out.set(part, offset); offset += part.length; });
    return out;
}

export function toHex(bytes) {
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

export function fromHex(hex) {
    if (typeof hex !== 'string' || hex.length % 2 !== 0 || /[^0-9a-f]/i.test(hex)) throw new Error('Hexadécimal invalide');
    const out = new Uint8Array(hex.length / 2);
    for (let i = 0; i < out.length; i += 1) out[i] = parseInt(hex.substr(i * 2, 2), 16);
    return out;
}

export function toBase64Url(bytes) {
    let binary = '';
    bytes.forEach(byte => { binary += String.fromCharCode(byte); });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(text) {
    if (typeof text !== 'string' || /[^A-Za-z0-9_-]/.test(text)) throw new Error('Base64url invalide');
    const padded = text.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(text.length / 4) * 4, '=');
    const binary = atob(padded);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
    return out;
}

export function randomBytes(length) {
    return globalThis.crypto.getRandomValues(new Uint8Array(length));
}

// ---------- primitives ----------

export async function sha256(bytes) {
    return new Uint8Array(await subtle().digest('SHA-256', bytes));
}

/** HKDF-SHA256, sel fixe SALT, `info` textuel. */
export async function hkdf(ikm, info, length = 32) {
    const key = await subtle().importKey('raw', ikm, 'HKDF', false, ['deriveBits']);
    const bits = await subtle().deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: utf8(SALT), info: utf8(info) }, key, length * 8);
    return new Uint8Array(bits);
}

async function aesKey(keyBytes, usages) {
    return subtle().importKey('raw', keyBytes, 'AES-GCM', false, usages);
}

/** AES-GCM : renvoie nonce(12) ‖ chiffré ‖ tag(16). */
export async function aesGcmSeal(keyBytes, plaintext, aad = new Uint8Array(0), nonce = randomBytes(NONCE_BYTES)) {
    const key = await aesKey(keyBytes, ['encrypt']);
    const sealed = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv: nonce, additionalData: aad, tagLength: 128 }, key, plaintext));
    return concatBytes(nonce, sealed);
}

export async function aesGcmOpen(keyBytes, payload, aad = new Uint8Array(0)) {
    if (payload.length < NONCE_BYTES + 16) throw new Error('Chiffré trop court');
    const key = await aesKey(keyBytes, ['decrypt']);
    const clear = await subtle().decrypt(
        { name: 'AES-GCM', iv: payload.slice(0, NONCE_BYTES), additionalData: aad, tagLength: 128 },
        key, payload.slice(NONCE_BYTES),
    );
    return new Uint8Array(clear);
}

// ---------- clé d'activité et sous-clés ----------

export function newActivityKey() {
    return randomBytes(32);
}

/**
 * Une clé par usage : on ne réutilise jamais la même clé pour GCM et HLS (CBC).
 * Sous-clés de 16 octets (AES-128) : c'est ce qu'acceptent le chiffrement
 * GCM natif Kotlin de l'app et le muxer HLS ; clé d'activité et enveloppes
 * restent en AES-256.
 */
export async function deriveSubkeys(activityKey) {
    return {
        events: await hkdf(activityKey, 'events', 16),
        messages: await hkdf(activityKey, 'messages', 16),
        mediaHls: await hkdf(activityKey, 'media-hls', 16),
    };
}

// Calculé à l'usage : certains environnements (jsdom) n'ont pas TextEncoder au chargement.
const gcmMagic = () => utf8('GCM1');

/** Format de contenu existant (« GCM1 » ‖ nonce ‖ chiffré ‖ tag, en hex), lisible par les lecteurs actuels. */
export async function encryptContent(subkey, text, nonce) {
    return toHex(concatBytes(gcmMagic(), await aesGcmSeal(subkey, utf8(text), new Uint8Array(0), nonce)));
}

export async function decryptContent(subkey, hex) {
    const bytes = fromHex(hex);
    if (bytes.length < 4 || toHex(bytes.slice(0, 4)) !== toHex(gcmMagic())) throw new Error('Format GCM1 attendu');
    return new TextDecoder().decode(await aesGcmOpen(subkey, bytes.slice(4)));
}

// ---------- lien de partage ----------

export function newLinkSecret() {
    return randomBytes(32);
}

/** Jeton présenté au serveur pour prouver qu'on a le lien, sans révéler la clé. */
export async function linkAccessToken(linkSecret) {
    return toBase64Url(await hkdf(linkSecret, 'access', 32));
}

/**
 * Jeton d'un porteur de mot de passe (lien police) : HKDF de la dérivation
 * PBKDF2 déjà utilisée pour l'enveloppe, avec l'info « access ». Le serveur
 * n'en garde que le SHA-256 ; il ne voit ni le mot de passe ni la clé.
 */
export async function passwordAccessToken(password, salt, iterations) {
    return toBase64Url(await hkdf(await passwordKek(password, salt, iterations), 'access', 32));
}

/** Mot de passe aléatoire de 20 caractères (≈ 100 bits), sans caractères ambigus. */
export function randomPassword(length = 20) {
    const alphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const out = [];
    while (out.length < length) {
        for (const byte of randomBytes(length * 2)) {
            // Rejet pour une distribution uniforme sur l'alphabet.
            if (byte < 256 - (256 % alphabet.length) && out.length < length) out.push(alphabet[byte % alphabet.length]);
        }
    }
    return out.join('');
}

/** Ce que le serveur stocke : SHA-256 (hex) du jeton, jamais le jeton. */
export async function accessTokenHash(token) {
    return toHex(await sha256(utf8(token)));
}

// ---------- clés P-256 ----------

const ECDH_PARAMS = { name: 'ECDH', namedCurve: 'P-256' };
const ECDSA_PARAMS = { name: 'ECDSA', namedCurve: 'P-256' };

export async function generateEcdhKeyPair() {
    return subtle().generateKey(ECDH_PARAMS, true, ['deriveBits']);
}

export async function importEcdhPublicSpki(spkiBytes) {
    return subtle().importKey('spki', spkiBytes, ECDH_PARAMS, true, []);
}

export async function importEcdhPrivatePkcs8(pkcs8Bytes) {
    return subtle().importKey('pkcs8', pkcs8Bytes, ECDH_PARAMS, false, ['deriveBits']);
}

async function ecdhSecret(privateKey, publicKey) {
    return new Uint8Array(await subtle().deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256));
}

export async function importEcdsaPublicSpki(spkiBytes) {
    return subtle().importKey('spki', spkiBytes, ECDSA_PARAMS, true, ['verify']);
}

/** Signature ECDSA P-256 / SHA-256 au format brut r ‖ s (64 octets). */
export async function verifySignature(publicKey, signature, data) {
    return subtle().verify({ name: 'ECDSA', hash: 'SHA-256' }, publicKey, signature, data);
}

// ---------- enveloppes ----------

const wrapInfo = type => ({ [RECIPIENT.DEVICE]: 'device-wrap', [RECIPIENT.ESCROW]: 'escrow-wrap', [RECIPIENT.LINK]: 'link-wrap' })[type];

function aadFor(header, activityId) {
    return concatBytes(header, utf8(String(activityId)));
}

function uint32(value) {
    return new Uint8Array([(value >>> 24) & 255, (value >>> 16) & 255, (value >>> 8) & 255, value & 255]);
}

/**
 * Enveloppe la clé d'activité pour une clé publique (appareil ou enquête).
 * `ephemeral` et `nonce` ne servent qu'aux vecteurs de test.
 */
export async function wrapForPublicKey(activityKey, recipientSpki, activityId, { type = RECIPIENT.DEVICE, ephemeral, nonce } = {}) {
    if (type !== RECIPIENT.DEVICE && type !== RECIPIENT.ESCROW) throw new Error('Type de destinataire invalide');
    const recipient = await importEcdhPublicSpki(recipientSpki);
    const pair = ephemeral || await generateEcdhKeyPair();
    const ephemeralRaw = new Uint8Array(await subtle().exportKey('raw', pair.publicKey));
    const kek = await hkdf(await ecdhSecret(pair.privateKey, recipient), wrapInfo(type), 32);
    const header = concatBytes(new Uint8Array([ENVELOPE_VERSION, type]), ephemeralRaw);
    return toBase64Url(concatBytes(header, await aesGcmSeal(kek, activityKey, aadFor(header, activityId), nonce)));
}

export async function wrapForLink(activityKey, linkSecret, activityId, { nonce } = {}) {
    const header = new Uint8Array([ENVELOPE_VERSION, RECIPIENT.LINK]);
    const kek = await hkdf(linkSecret, 'link-wrap', 32);
    return toBase64Url(concatBytes(header, await aesGcmSeal(kek, activityKey, aadFor(header, activityId), nonce)));
}

export async function passwordKek(password, salt, iterations) {
    const key = await subtle().importKey('raw', utf8(password), 'PBKDF2', false, ['deriveBits']);
    return new Uint8Array(await subtle().deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, key, 256));
}

export async function wrapWithPassword(activityKey, password, activityId, { salt = randomBytes(16), iterations = PASSWORD_ITERATIONS, nonce } = {}) {
    const header = concatBytes(new Uint8Array([ENVELOPE_VERSION, RECIPIENT.PASSWORD]), salt, uint32(iterations));
    const kek = await passwordKek(password, salt, iterations);
    return toBase64Url(concatBytes(header, await aesGcmSeal(kek, activityKey, aadFor(header, activityId), nonce)));
}

/** Lit l'en-tête d'une enveloppe sans l'ouvrir. */
export function parseEnvelope(envelope) {
    const bytes = fromBase64Url(envelope);
    if (bytes[0] !== ENVELOPE_VERSION) throw new Error('Version d’enveloppe inconnue');
    const type = bytes[1];
    let headerLength = 2;
    const parsed = { type, bytes };
    if (type === RECIPIENT.DEVICE || type === RECIPIENT.ESCROW) {
        parsed.ephemeralRaw = bytes.slice(2, 2 + RAW_P256_PUBLIC_BYTES);
        headerLength += RAW_P256_PUBLIC_BYTES;
    } else if (type === RECIPIENT.PASSWORD) {
        parsed.salt = bytes.slice(2, 18);
        const view = bytes.slice(18, 22);
        parsed.iterations = ((view[0] << 24) >>> 0) + (view[1] << 16) + (view[2] << 8) + view[3];
        headerLength += 20;
    } else if (type !== RECIPIENT.LINK) {
        throw new Error('Type d’enveloppe inconnu');
    }
    parsed.header = bytes.slice(0, headerLength);
    parsed.sealed = bytes.slice(headerLength);
    return parsed;
}

/**
 * Ouvre une enveloppe. `opener` : { linkSecret } | { password } | { privateKey } (CryptoKey ECDH).
 */
export async function unwrap(envelope, activityId, opener) {
    const parsed = parseEnvelope(envelope);
    const aad = aadFor(parsed.header, activityId);
    let kek;
    if (parsed.type === RECIPIENT.LINK) {
        if (!opener.linkSecret) throw new Error('Secret de lien requis');
        kek = await hkdf(opener.linkSecret, 'link-wrap', 32);
    } else if (parsed.type === RECIPIENT.PASSWORD) {
        if (typeof opener.password !== 'string') throw new Error('Mot de passe requis');
        if (parsed.iterations < 100_000 && !opener.allowWeakIterations) throw new Error('Enveloppe mot de passe trop faible');
        kek = await passwordKek(opener.password, parsed.salt, parsed.iterations);
    } else {
        if (!opener.privateKey) throw new Error('Clé privée requise');
        const ephemeral = await subtle().importKey('raw', parsed.ephemeralRaw, ECDH_PARAMS, true, []);
        kek = await hkdf(await ecdhSecret(opener.privateKey, ephemeral), wrapInfo(parsed.type), 32);
    }
    const activityKey = await aesGcmOpen(kek, parsed.sealed, aad);
    if (activityKey.length !== 32) throw new Error('Clé d’activité invalide');
    return activityKey;
}
